<template>
  <app-modal
    v-model="isNewsletterModalActive"
    :title="isSubscribed ? $t('newsletter_pending_title') : $t('general_receive_newsletter')"
    body-class="space-y-3"
    :confirm-label="isSubscribed ? '' : $t('general_button_signup_newsletter')"
    :confirm-loading="isLoading"
    :confirm-disabled="!contactForm.email"
    :cancel-label="isSubscribed ? $t('general_button_close') : ''"
    :close-on-confirm="false"
    show-cancel
    @cancel="closeModal"
    @confirm="confirmModal"
  >
    <template v-if="isSubscribed">
      <p>{{ $t('newsletter_pending_body') }}</p>
      <!-- ponytail: one link, always external. `newsUrl` is whatever the API
           says the mail links to, so there is no second copy of the URL here
           and no in-app-vs-archive branch to keep in sync. -->
      <text-link
        :href="newsUrl"
        target="_blank"
        :label="$t('newsletter_read_latest_news')"
        :icon="mdiOpenInNew"
      />
    </template>
    <template v-else>
      <p>{{ $t('newsletter_introduction') }}</p>
      <app-field :label="$t('general_name')" :help="$t('general_name_help_required')">
        <app-input name="firstname" :placeholder="$t('general_firstname')" type="text" required v-model="contactForm.firstname" container-class="mb-0"/>
        <app-input name="lastname" :placeholder="$t('general_lastname')" type="text" required v-model="contactForm.lastname" container-class="mb-0"/>
      </app-field>
      <app-input
        name="email"
        type="email"
        autocomplete="email"
        required
        :label="$t('general_email')"
        :help="$t('general_email_help_required')"
        :error="errorEmail"
        v-model="contactForm.email"
      />
    </template>
    <template #footer-start>
      <text-link to="/contact" :label="$t('general_button_ask_for_help')" :icon="mdiLifebuoy" class="text-sm" />
    </template>
  </app-modal>
</template>

<script>
import { useNotification } from '@/composables/useNotification'
import { computed, reactive, ref } from 'vue'
import { mdiLifebuoy, mdiOpenInNew } from '@mdi/js'
import AppField from '@/components/ui/forms/AppField.vue'
import AppInput from '@/components/ui/forms/AppInput.vue'
import AppModal from '@/components/ui/overlays/AppModal.vue'
import TextLink from '@/components/ui/layout/TextLink'
import EmailRepository from '@/repositories/EmailRepository'
import { useI18n } from 'vue-i18n'
import { useDisplayStore } from '@/stores/useDisplayStore';
import { useAuthStore } from '@/stores/useAuthStore';

export default {
  name: 'ModalNewsletter',
  components: {
    AppField,
    AppInput,
    AppModal,
    TextLink
  },
  setup () {
    const { t } = useI18n()
    const authStore = useAuthStore();
    const displayStore = useDisplayStore();
    const { notifyError } = useNotification()
    const isLoading = ref(false)
    const isSubscribed = ref(false)
    const newsUrl = ref('')

    const contactForm = reactive({
      firstname: '',
      lastname: '',
      email: authStore.userEmail
    })

    const errorEmail = ref('')

    // Computed value
    const isNewsletterModalActive = computed({
      get: () => displayStore.isNewsletterModalActive,
      set: value => displayStore.newsletterModalToggle(value)
    })

    // Close modal through user action. Reset so reopening starts on the form
    // rather than on a stale confirmation.
    const closeModal = () => {
      displayStore.newsletterModalToggle(false)
      isSubscribed.value = false
      errorEmail.value = ''
    }

    // FastAPI answers a validation failure with a LIST of {loc, msg} objects;
    // rendering that straight into a toast produced "[object Object]".
    const errorMessage = (error) => {
      const detail = error?.response?.data?.detail
      if (Array.isArray(detail)) return detail[0]?.msg || t('messages.server_error')
      return detail || t('messages.server_error')
    }

    const confirmModal = () => {
      if (isLoading.value) return
      isLoading.value = true
      errorEmail.value = ''

      EmailRepository.subscribeNewsletter(contactForm).then(response => {
        // Stay open and switch to the confirmation panel — the modal used to
        // close on click (AppModal's `closeOnConfirm` default), so the success
        // toast landed half a second later with no dialog to belong to (PI-166).
        newsUrl.value = response.data?.news_url || ''
        isSubscribed.value = true
      }).catch((error) => {
        // Keep the modal open on failure, so the address is still there to fix.
        errorEmail.value = errorMessage(error)
        notifyError(errorEmail.value)
      }).finally(() => {
        isLoading.value = false
      })
    }

    return {
      closeModal,
      contactForm,
      confirmModal,
      errorEmail,
      isLoading,
      isNewsletterModalActive,
      isSubscribed,
      newsUrl,
      mdiOpenInNew,
      mdiLifebuoy
    }
  }
}
</script>
